/**
 * Referral attribution capture.
 *
 * A visitor arriving on /r/:code (or any page with ?ref=) gets a first-party
 * cookie that survives for the attribution window. The code is claimed against
 * the backend the first time that visitor is authenticated, which is what
 * actually creates the attribution — the cookie is only a carrier.
 */

const COOKIE_NAME = 'lh_ref'
const CLICKED_AT_COOKIE = 'lh_ref_at'
/** Mirrors REFERRAL_COOKIE_DAYS in lighthouse-backend config. */
const COOKIE_DAYS = 60
/** Fallback for browsers/webviews where cookies are unavailable. */
const STORAGE_KEY = 'lh_ref'
const STORAGE_CLICKED_AT_KEY = 'lh_ref_at'

export interface StoredReferral {
    code: string
    clickedAt: string | null
}

function writeCookie(name: string, value: string, days: number): void {
    try {
        const expires = new Date(Date.now() + days * 86400 * 1000).toUTCString()
        const secure = location.protocol === 'https:' ? '; Secure' : ''
        document.cookie = `${name}=${encodeURIComponent(value)}; path=/; expires=${expires}; SameSite=Lax${secure}`
    } catch {
        // ignore — the localStorage fallback covers this
    }
}

function readCookie(name: string): string | null {
    try {
        const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
        return match ? decodeURIComponent(match[1]) : null
    } catch {
        return null
    }
}

function clearCookie(name: string): void {
    try {
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`
    } catch {
        // ignore
    }
}

/** Codes are 8 chars from an unambiguous alphabet (see partners.py). */
/**
 * Codes are case-insensitive (stored uppercase). Generated codes are 8
 * alphanumerics; custom ones are 4-20 letters, digits and single hyphens.
 * Keep in step with validate_custom_code in the backend's referrals/partners.py.
 */
export function normalizeReferralCode(raw: string | null | undefined): string | null {
    const cleaned = (raw || '').trim().toUpperCase()
    if (cleaned.length < 4 || cleaned.length > 20) return null
    if (!/^[0-9A-Z]+(?:-[0-9A-Z]+)*$/.test(cleaned)) return null
    return cleaned
}

/** Persist a referral code. First touch wins, so an existing code is kept. */
export function stashReferralCode(raw: string | null | undefined): string | null {
    const code = normalizeReferralCode(raw)
    if (!code) return null

    const existing = peekReferral()
    if (existing) return existing.code

    const clickedAt = new Date().toISOString()
    writeCookie(COOKIE_NAME, code, COOKIE_DAYS)
    writeCookie(CLICKED_AT_COOKIE, clickedAt, COOKIE_DAYS)
    try {
        localStorage.setItem(STORAGE_KEY, code)
        localStorage.setItem(STORAGE_CLICKED_AT_KEY, clickedAt)
    } catch {
        // ignore quota / private-mode failures
    }
    return code
}

export function peekReferral(): StoredReferral | null {
    let code = normalizeReferralCode(readCookie(COOKIE_NAME))
    let clickedAt = readCookie(CLICKED_AT_COOKIE)

    if (!code) {
        try {
            code = normalizeReferralCode(localStorage.getItem(STORAGE_KEY))
            clickedAt = clickedAt || localStorage.getItem(STORAGE_CLICKED_AT_KEY)
        } catch {
            return null
        }
    }
    if (!code) return null
    return { code, clickedAt: clickedAt || null }
}

export function clearReferral(): void {
    clearCookie(COOKIE_NAME)
    clearCookie(CLICKED_AT_COOKIE)
    try {
        localStorage.removeItem(STORAGE_KEY)
        localStorage.removeItem(STORAGE_CLICKED_AT_KEY)
    } catch {
        // ignore
    }
}

/** Pick up ?ref= / ?refer= on any public page, not just the /r/ redirect. */
export function captureReferralFromQuery(
    query: Record<string, unknown>,
): string | null {
    const raw = query.ref ?? query.refer ?? query.referral
    if (typeof raw !== 'string') return null
    return stashReferralCode(raw)
}

/**
 * Claim the stored code for the freshly authenticated user.
 *
 * The backend enforces the real rules (first touch, self-referral, window), so
 * a rejection here is expected for existing users and simply clears the cookie.
 */
export async function claimStoredReferral(): Promise<boolean> {
    const stored = peekReferral()
    if (!stored) return false

    const { attributeReferral } = await import('@/api/referrals.api')
    const attributed = await attributeReferral(stored.code, stored.clickedAt)
    // Either way the code has had its chance; keeping it would only risk it
    // being claimed by a different account on a shared browser.
    clearReferral()
    return attributed
}
